import { useEffect, useState, useMemo, useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';
import { FormMultiSelect } from '@fashionnovateam/nova-ui-toolkit';
import { AddTagFormData } from '../types';
import { useGetUserPurchaseOrders } from '../../../../Lib/Hooks/purchase-orders.hooks';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';

export function POAndProductSection() {
  const { control, watch, setValue } = useFormContext<AddTagFormData>();
  const { id: userId } = useParams<{ id: string }>();

  const poNumber = watch('poNumber');
  const selectedProduct = watch('selectedProduct');
  const currentImageUrl = watch('productImageUrl');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [dropdownKey, setDropdownKey] = useState(0);
  const prevPoNumberRef = useRef<string | undefined>(poNumber);
  const prevPOImageRef = useRef<string | undefined>(undefined);
  const prevSelectedPONumberRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const handler = debounce(() => setDebouncedSearchTerm(searchTerm), 1500);
    handler();
    return () => handler.cancel();
  }, [searchTerm]);

  useEffect(() => {
    const prevPoNumber = prevPoNumberRef.current;
    prevPoNumberRef.current = poNumber;

    if (
      prevPoNumber !== undefined &&
      prevPoNumber !== poNumber &&
      watch('selectedProduct')
    ) {
      setValue('selectedProduct', '');
    }
  }, [poNumber, setValue, watch]);

  const { data: purchaseOrdersData, isLoading: isLoadingPOs } =
    useGetUserPurchaseOrders(userId || '', {
      page: 0,
      size: debouncedSearchTerm ? 500 : 50,
      search: debouncedSearchTerm || poNumber || undefined,
      filter: {},
    });

  const selectedPO = purchaseOrdersData?.purchaseOrders?.find(
    (po) => po.po_number === poNumber,
  );

  const productOptions = useMemo(
    () =>
      selectedPO?.purchase_order_lines
        ?.filter((line) => line.sku)
        ?.map((line) => ({ label: line.sku!, value: line.sku! })) || [],
    [selectedPO],
  );

  const selectedProductLine = useMemo(
    () =>
      selectedPO?.purchase_order_lines?.find(
        (line) => line.sku === selectedProduct,
      ),
    [selectedPO, selectedProduct],
  );

  useEffect(() => {
    if (!selectedProductLine) return;

    setValue('poLineName', selectedProductLine.item_name);
    setValue('poLineColor', selectedProductLine.color_label);
    setValue('poLineSize', selectedProductLine.size);
    setValue('styleNumber', selectedProductLine.sku?.split('_')[0]);
  }, [selectedProductLine, setValue]);

  useEffect(() => {
    if (!selectedPO) return;

    const poImage = selectedPO.images?.[0];
    const poChanged = prevSelectedPONumberRef.current !== selectedPO.po_number;

    if (poImage) {
      if (poChanged || currentImageUrl !== poImage) {
        setValue('productImageUrl', poImage);
        prevPOImageRef.current = poImage;
      }
    } else {
      if (poChanged) {
        setValue('productImageUrl', '');
      }
      prevPOImageRef.current = undefined;
    }

    prevSelectedPONumberRef.current = selectedPO.po_number;

    if (selectedPO.due_date) {
      setValue('poDueDate', selectedPO.due_date);
    }
  }, [selectedPO, setValue, currentImageUrl]);

  const getPOPlaceholder = () => {
    if (isLoadingPOs) return 'Loading purchase orders...';
    if (debouncedSearchTerm) {
      return `Found ${purchaseOrdersData?.purchaseOrders?.length || 0} results`;
    }
    return 'Search purchase orders...';
  };

  const getProductPlaceholder = () => {
    if (isLoadingPOs && poNumber) return 'Loading products...';
    if (productOptions.length > 0) return 'Select...';
    return 'Select a PO first';
  };

  const poValidationRules = {
    required: 'PO Number is required',
    validate: (value: string | undefined) =>
      value?.trim() ? true : 'Please select a PO Number',
  };

  return (
    <div className="rounded-xl border border-neutral-200 shadow-sm p-5 space-y-4">
      <Controller
        name="poNumber"
        control={control}
        rules={poValidationRules}
        render={({ field, fieldState }) => (
          <FormMultiSelect
            key={dropdownKey}
            name="poNumber"
            label="PO Number"
            searchable
            singleSelectWhenSearchable
            placeholder={getPOPlaceholder()}
            onSearch={setSearchTerm}
            value={field.value ? [field.value] : []}
            onChange={(selectedValues) => {
              field.onChange(selectedValues[0] || '');
              setSearchTerm('');
              setDropdownKey((prev) => prev + 1);
            }}
            error={fieldState.error?.message}
            options={
              purchaseOrdersData?.purchaseOrders?.map((po) => ({
                label: po.po_number,
                value: po.po_number,
              })) || []
            }
          />
        )}
      />

      <ControlledFormInput
        name="selectedProduct"
        label="Select Product for Sample Tag"
        control={control}
        inputType="select"
        required
        inputProps={{
          placeholder: getProductPlaceholder(),
          className: 'w-full',
          options: productOptions,
          disabled: productOptions.length === 0,
        }}
      />
    </div>
  );
}
